import * as commonHooks from './common';
import * as dcHooks from './dc';
import * as roasHooks from './roas';
import * as bidHooks from './bid';
import * as deepBidHooks from './deep-bid';
import * as strategyTooltipHooks from './strategy-tooltip';
import * as suggestHooks from './suggest';
import * as unifiedBidStrategy from './unified-bid-strategy';

import {
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  AppEvents,
  SmartBidType,
  PricingType,
} from '@tt4b/ttam-enums';
import {
  useStdAdgroupOptimizationGoal,
  useAdgroupAppEvents,
  useStdAdgroupIs,
  useStdObjectiveType,
  useAllowlist,
  useStdAdgroupCbo,
} from '@tt4b/ttam-hooks';
import { Ref, computed } from '@vue/composition-api';
import { i18n } from './../../../../i18n';

/** VideoViewTips  */
export function useVideoViewTipsPropsVisible(props: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = props;
  const { isOptGoalVideoView } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });
  return {
    visible: computed(() => isOptGoalVideoView.value),
  };
}

/** LearningPhaseTips  */
export function useLearningPhaseTipsPropsVisible(props: {
  adgroupConfig: Ref<AdgroupConfig>;
  isLearningPhase: Ref<boolean>;
}) {
  const { isLearningPhase, adgroupConfig } = props;
  return {
    visible: computed(() => isLearningPhase.value && adgroupConfig.value?.showLearningPhaseHint),
  };
}

/**  OptgoalInappeventTitle */

export function useOptgoalInappeventTitlePropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData } = props;
  const { isOptGoalInAppEvent } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });
  return {
    visible: computed(() => isOptGoalInAppEvent.value),
  };
}

/**  OptgoalInstallWithInappeventTitle */

export function useOptgoalInstallWithInappeventTitlePropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { adgroupData, campaignData, appEvents } = props;
  const { isInstallWithInAppEvent } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    appEvents,
    campaignFormData: campaignData,
  });
  return {
    visible: computed(() => isInstallWithInAppEvent.value),
  };
}

export function useOptgoalInstallWithInappeventTitlePropsTips(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { adgroupData, campaignData, appEvents } = props;
  const { curDeepExternalActionItem } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    appEvents,
    campaignFormData: campaignData,
  });

  const _deepEvent = computed(() => i18n.t(curDeepExternalActionItem.value?.external_action_i18nkey || '') as string);

  const tips = computed(() => i18n.t('module_bidding_cap_the_cost_of_install_tip', { deepEvent: _deepEvent }));

  return {
    tips,
  };
}

export function useInitDataGet(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { adgroupData, campaignData } = props;

  const { isCBOCostCapWhite } = useAllowlist();

  const { isCLA, isSPC } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isProductSales, isWebConversion, isLeadAds } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const pricing = adgroupData.value?.pricing;

  let smartBidType = [PricingType.CPC, PricingType.CPM, PricingType.CPV].includes(pricing)
    ? SmartBidType.NO_BID
    : SmartBidType.STANDARD;

  if (isWebConversion.value || isLeadAds.value || isProductSales.value) {
    smartBidType = SmartBidType.NO_BID;
  }

  // CLA不支持no-bid
  if (isCLA.value) {
    smartBidType = SmartBidType.STANDARD;
  }

  if (
    isCBO.value &&
    !isSPC.value &&
    [PricingType.OCPC, PricingType.OCPM].includes(pricing) &&
    !isCBOCostCapWhite.value
  ) {
    smartBidType = SmartBidType.NO_BID;
  }

  return {
    smartBidType,
  };
}

export default {
  ...commonHooks,
  ...dcHooks,
  ...roasHooks,
  ...bidHooks,
  ...deepBidHooks,
  ...suggestHooks,
  ...strategyTooltipHooks,
  ...unifiedBidStrategy,
  useVideoViewTipsPropsVisible,
  useLearningPhaseTipsPropsVisible,
  useOptgoalInstallWithInappeventTitlePropsVisible,
  useOptgoalInstallWithInappeventTitlePropsTips,
  useOptgoalInappeventTitlePropsVisible,
  useInitDataGet,
};
